/**
 * Shared capabilities span panels (spec 2026-10-18 AI companion growth
 * architecture, 4.4). Chris, 2026-10-18: filters and columns have to be
 * available on many different panels, so they are capabilities panels share,
 * not tools a panel owns.
 *
 * Each surface (a panel type, or a page with its own filters) registers a
 * capability together with the adapter that implements it, in one call: the
 * declaration reaches the tool registry, which offers the filter and column
 * tools wherever such a surface is on screen, and the adapter is what those
 * tools run. A surface without an adapter declares nothing; the drift test
 * (panel-capabilities.unit.test.ts) lists the ones still waiting for one.
 *
 * Adapters are async: a panel's own column definitions load with that panel's
 * chunk, so an adapter fetches them on first use instead of pulling every
 * panel's catalog into the app's first load.
 */
import { declarePageCapability, declarePanelCapability, type Operation } from '@edge/edge-ai'
import { getPanelLiveState } from '@/stores/panel-state-registry'
import { CALENDAR_COLUMN_MAP } from '@/features/calendar/schemas/calendar-column-defs'
import {
  SCANNER_COLUMN_MAP,
  DEFAULT_SCANNER_COLUMNS,
} from '@/features/scanner/schemas/scanner-column-defs'
import { DEFAULT_SCANNER_FILTERS } from '@/features/scanner/schemas/scanner-defaults'
import {
  DEFAULT_HISTORY_RECORDS_COLUMNS,
  DEFAULT_RECORDS_COLUMNS,
  HISTORY_COLUMN_MAP,
} from '@/features/ticker-stats/schemas/history-column-defs'
import type { ColumnCatalogDef } from '@/types/column-catalog'
import { getColumnCatalogText } from './column-catalogs'
import {
  buildFilters,
  filterColumnsOf,
  type AIFilterInput,
  type ColumnTypeInfo,
} from './filter-builder'
import { lastPressedOf } from './last-panel-tracker'
import { mergeFilterLeaves } from './merge-filter-leaves'
import type { ActionCallbacks, SetHistoryFiltersResult } from './types'

export type CapabilityResult = { ok: true; message: string } | { ok: false; error: string }

/** The shared filter contract: a surface takes filters in the AI's flat shape. */
export interface FilterAdapter {
  /** Whether it can remove every filter. History's page filter cannot: its date scope stays. */
  clears: boolean
  /** Replace the filter on each column sent; filters on other columns stay. */
  set(
    target: string,
    inputs: AIFilterInput[],
    callbacks: ActionCallbacks,
  ): Promise<CapabilityResult>
  /** Remove every filter. */
  clear(target: string, callbacks: ActionCallbacks): Promise<CapabilityResult>
}

/** A page's own filters, there only while that page's callbacks are. */
export interface PageFilterAdapter extends FilterAdapter {
  available(callbacks: ActionCallbacks): boolean
}

/** A panel's shown columns, loaded for one call. */
export interface PanelColumns {
  current: string[]
  /** The keys the panel accepts, when it knows them. */
  validKeys: string[] | undefined
  /** Columns that always stay, first (the ticker; the Economic calendar's event). */
  fixed: readonly string[]
  write(columns: string[]): void
}

/** The shared column contract: the column keys a panel shows, read and written. */
export interface ColumnAdapter {
  load(panelId: string, callbacks: ActionCallbacks): Promise<PanelColumns | { error: string }>
}

const panelFilters = new Map<string, FilterAdapter>()
const panelColumns = new Map<string, ColumnAdapter>()
const pageFilters = new Map<string, PageFilterAdapter>()

function filterOperations(adapter: FilterAdapter): Operation[] {
  return adapter.clears ? ['add', 'change', 'remove'] : ['add', 'change']
}

/** A panel type implements filters with this adapter. */
export function registerPanelFilters(panelType: string, adapter: FilterAdapter): void {
  panelFilters.set(panelType, adapter)
  declarePanelCapability(panelType, 'filters', filterOperations(adapter))
}

/** A panel type implements columns with this adapter. */
export function registerPanelColumns(panelType: string, adapter: ColumnAdapter): void {
  panelColumns.set(panelType, adapter)
  declarePanelCapability(panelType, 'columns', ['add', 'remove', 'change'])
}

/** A page implements filters itself, with this adapter. */
export function registerPageFilters(page: string, adapter: PageFilterAdapter): void {
  pageFilters.set(page, adapter)
  declarePageCapability(page, 'filters', filterOperations(adapter))
}

/** The panel types that implement a capability. */
export function panelTypesWith(capability: 'filters' | 'columns'): string[] {
  return [...(capability === 'filters' ? panelFilters : panelColumns).keys()]
}

// ── Where a call lands ──────────────────────────────────────────────────────

export type FilterTarget =
  | { kind: 'panel'; id: string; type: string; adapter: FilterAdapter }
  | { kind: 'page'; id: string; adapter: PageFilterAdapter }

export interface ColumnTarget {
  id: string
  type: string
  adapter: ColumnAdapter
}

/** The open panels of every type in `adapters`, each with its adapter. */
function openPanels<A>(adapters: Map<string, A>, callbacks: ActionCallbacks) {
  return [...adapters].flatMap(([type, adapter]) =>
    (callbacks.getPanelsByType?.(type) ?? []).map((p) => ({ id: p.panelId, type, adapter })),
  )
}

/**
 * Resolved like a chart target: the panel named, else the open one the trader
 * last pressed, else the only one.
 */
function pickPanel<T extends { id: string; type: string }>(
  open: T[],
  requested: unknown,
): T | 'none' | { error: string } {
  if (typeof requested === 'string' && requested)
    return open.find((p) => p.id === requested) ?? 'none'
  if (open.length === 0) return 'none'
  const pressed = lastPressedOf(open.map((p) => p.id))
  const pick = open.find((p) => p.id === pressed) ?? (open.length === 1 ? open[0] : undefined)
  return (
    pick ?? {
      error: `Multiple panels found (${open.map((p) => `${p.type}:${p.id}`).join(', ')}). Call request_panel_selection or specify panel_id.`,
    }
  )
}

/** "No scanner panel found." for the panel types that implement a capability. */
function noneFound(types: string[]): string {
  const list = types.length > 1 ? `${types.slice(0, -1).join(', ')} or ${types.at(-1)}` : types[0]
  return `No ${list} panel found.`
}

/**
 * Where a filter call lands: a panel with filters (named, last pressed, or the
 * only one), else the page's own filters (History's, which every history panel
 * shows, so a history panel named lands there too). `clear` keeps to surfaces
 * that can clear; `page` goes straight to the page (the old set_history_filters).
 * The executor and the Undo snapshot resolve the same target.
 */
export function resolveFilterTarget(
  callbacks: ActionCallbacks,
  requested?: unknown,
  opts: { clear?: boolean; page?: boolean } = {},
): FilterTarget | { error: string } {
  const page = [...pageFilters].find(
    ([, adapter]) => adapter.available(callbacks) && (!opts.clear || adapter.clears),
  )
  const pageTarget = page ? ({ kind: 'page', id: page[0], adapter: page[1] } as const) : null
  if (opts.page) return pageTarget ?? { error: 'setHistoryFilters is not available on this page' }
  const adapters = new Map([...panelFilters].filter(([, a]) => !opts.clear || a.clears))
  const pick = pickPanel(openPanels(adapters, callbacks), requested)
  if (pick !== 'none') return 'error' in pick ? pick : { kind: 'panel', ...pick }
  if (pageTarget) return pageTarget
  return typeof requested === 'string' && requested
    ? { error: `Panel ${requested} has no filters the AI can change.` }
    : { error: noneFound([...adapters.keys()]) }
}

/** Where a column call lands: a panel with columns, named, last pressed, or the only one. */
export function resolveColumnTarget(
  callbacks: ActionCallbacks,
  requested?: unknown,
): ColumnTarget | { error: string } {
  const pick = pickPanel(openPanels(panelColumns, callbacks), requested)
  if (pick !== 'none') return pick
  return typeof requested === 'string' && requested
    ? { error: `Panel ${requested} has no columns the AI can change.` }
    : { error: noneFound(panelTypesWith('columns')) }
}

// ── Adapters over a panel's stored state ────────────────────────────────────

type PanelState = Record<string, unknown>

/** Where a panel keeps one set of filters, and what they may name. */
export interface FilterSlot {
  filtersKey: string
  profileIdKey: string
  /** The filter columns. Keys and types the panel publishes live win. */
  columns: Record<string, ColumnCatalogDef>
  /** What the panel filters on when it has stored none. */
  defaults?: unknown
  /** Columns the panel keeps itself, each with where the trader changes it. */
  fixed?: Record<string, string>
  /** Written beside the filters, as the panel's own Apply does (Multi-Chart's scan name). */
  alsoWrite?: Record<string, unknown>
  /** Named in the result when the panel has several sets (Calendar's tabs). */
  label?: string
  /** What a clear stores: the panel's empty filters in its own shape. Default: an empty query group. */
  emptyFilters?: unknown
  /** A clear on a panel with no stored filters and no defaults writes nothing (it already has none). */
  clearKeepsNone?: boolean
}

/** Where a panel keeps its shown columns. */
export interface ColumnSlot {
  columnsKey: string
  profileIdKey: string
  /** The panel's columns when it has stored none. */
  defaults: readonly string[]
  /** What the panel shows from what it stored, when that differs (retired keys dropped). */
  shown?: (stored: string[] | undefined) => string[]
  /** The columns it can show. */
  columns: Record<string, ColumnCatalogDef>
  fixed: readonly string[]
}

type SlotFor<S> = (state: PanelState) => Promise<S | { error: string }>

function where(panelId: string, label?: string): string {
  return label ? `panel ${panelId} (${label})` : `panel ${panelId}`
}

/**
 * Filters a panel stores in its own state (the form's shape or a query group;
 * mergeFilterLeaves keeps whichever it finds). Merged into what the panel shows,
 * its defaults included, so "add a float filter" on a fresh Scanner keeps its
 * common-stock filter.
 */
function storedFilters(panelType: string, slotFor: SlotFor<FilterSlot>): FilterAdapter {
  async function load(panelId: string, callbacks: ActionCallbacks) {
    if (!callbacks.updatePanel) return { error: 'updatePanel is not available on this page' }
    const state = (callbacks.getPanel?.(panelId) ?? {}) as PanelState
    const slot = await slotFor(state)
    return 'error' in slot ? slot : { slot, state, update: callbacks.updatePanel }
  }
  return {
    clears: true,
    async set(panelId, inputs, callbacks) {
      const loaded = await load(panelId, callbacks)
      if ('error' in loaded) return { ok: false, error: loaded.error }
      const { slot, state, update } = loaded
      const kept = inputs.find((i) => slot.fixed?.[i.left?.column])
      if (kept) return { ok: false, error: slot.fixed![kept.left.column] }
      const live = getPanelLiveState(panelId)
      const known = filterColumnsOf(slot.columns)
      const result = buildFilters(
        inputs,
        (live?._validColumnKeys as string[] | undefined) ?? known.keys,
        (live?._columnTypeMap as Record<string, ColumnTypeInfo> | undefined) ?? known.types,
      )
      if (!result.success) {
        // On column-validation errors, attach the panel's catalog inline so the
        // model can correct the key on its next turn without a search_knowledge
        // round trip (which can 503).
        const isColumnError = /Invalid column|Invalid right column/i.test(result.error)
        const catalog = isColumnError ? getColumnCatalogText(panelType) : ''
        return { ok: false, error: catalog ? `${result.error}\n\n${catalog}` : result.error }
      }
      const stored = state[slot.filtersKey] ?? slot.defaults
      update(panelId, {
        [slot.filtersKey]: mergeFilterLeaves(stored, (result.filters.AND as unknown[]) ?? []),
        [slot.profileIdKey]: null,
        ...slot.alsoWrite,
      })
      const columns = [...new Set(inputs.map((i) => i.left.column))].join(', ')
      return {
        ok: true,
        message: `Set filters for ${columns} on ${where(panelId, slot.label)}. Filters on other columns preserved.`,
      }
    },
    async clear(panelId, callbacks) {
      const loaded = await load(panelId, callbacks)
      if ('error' in loaded) return { ok: false, error: loaded.error }
      const { slot, state, update } = loaded
      if (
        slot.clearKeepsNone &&
        state[slot.filtersKey] === undefined &&
        slot.defaults === undefined
      )
        return { ok: true, message: `No filters to clear on ${where(panelId, slot.label)}` }
      update(panelId, {
        [slot.filtersKey]: slot.emptyFilters ?? { AND: [] },
        [slot.profileIdKey]: null,
        ...slot.alsoWrite,
      })
      return { ok: true, message: `All filters cleared on ${where(panelId, slot.label)}` }
    },
  }
}

/** Columns a panel stores in its own state, read with its defaults. */
function storedColumns(slotFor: SlotFor<ColumnSlot>): ColumnAdapter {
  return {
    async load(panelId, callbacks) {
      const state = (callbacks.getPanel?.(panelId) ?? {}) as PanelState
      const slot = await slotFor(state)
      if ('error' in slot) return slot
      const stored = state[slot.columnsKey] as string[] | undefined
      return {
        current: slot.shown ? slot.shown(stored) : (stored ?? [...slot.defaults]),
        validKeys:
          (getPanelLiveState(panelId)?._validColumnKeys as string[] | undefined) ??
          Object.keys(slot.columns),
        fixed: slot.fixed,
        write: (columns) =>
          callbacks.updatePanel?.(panelId, {
            [slot.columnsKey]: columns,
            [slot.profileIdKey]: null,
          }),
      }
    },
  }
}

/** The usual keys: `filters` with `filterProfileId`. */
const FILTERS = { filtersKey: 'filters', profileIdKey: 'filterProfileId' }
/** The usual keys: `selectedColumns` with `columnProfileId`, the ticker kept first. */
const COLUMNS = {
  columnsKey: 'selectedColumns',
  profileIdKey: 'columnProfileId',
  fixed: ['ticker'],
}

/** A page callback's answer as the tool's result; a column error carries the catalog. */
function pageResult(
  result: SetHistoryFiltersResult,
  done: (chips: string) => string,
): CapabilityResult {
  if (!result.success)
    return {
      ok: false,
      error: result.catalogHint ? `${result.error}\n\n${result.catalogHint}` : result.error,
    }
  return { ok: true, message: done(result.appliedChips.join(', ')) }
}

/**
 * The History page's page-wide filters (`globalSettings.historyFilters`), which
 * every history panel reacts to. The page's `setHistoryFilters` callback holds
 * the layout store, validates against the history catalog and records the
 * change as asked of the AI. No clear: the page keeps its date scope.
 */
const historyPageFilters: PageFilterAdapter = {
  clears: false,
  available: (callbacks) => Boolean(callbacks.setHistoryFilters),
  async set(_page, inputs, callbacks) {
    return pageResult(
      callbacks.setHistoryFilters!(inputs),
      (chips) => `Set page-wide History filters: ${chips}. All history panels now reflect this.`,
    )
  },
  async clear() {
    return {
      ok: false,
      error:
        'The History page keeps its filters and date scope; set a column to change its filter.',
    }
  },
}

/**
 * The Playbook's filters: the play's own, which every card reads. The page's
 * `pageFilters` callbacks validate against its sheet's columns and merge as the
 * reader's sheet does. No clear: the page's reset is back to the play's own.
 */
const playbookFilters: PageFilterAdapter = {
  clears: false,
  available: (callbacks) => callbacks.pageFilters?.page === 'playbook',
  async set(_page, inputs, callbacks) {
    return pageResult(
      callbacks.pageFilters!.set(inputs),
      (chips) => `Set the play's filters: ${chips}. Every card now reads them.`,
    )
  },
  async clear() {
    return {
      ok: false,
      error:
        "The Playbook keeps a play's filters; set a column to change one, or the reader resets them to the play's own.",
    }
  },
}

// ── Registrations ───────────────────────────────────────────────────────────

registerPageFilters('history', historyPageFilters)
registerPageFilters('playbook', playbookFilters)

registerPanelFilters(
  'scanner',
  storedFilters('scanner', async () => ({
    ...FILTERS,
    columns: SCANNER_COLUMN_MAP,
    defaults: DEFAULT_SCANNER_FILTERS,
  })),
)

for (const type of ['scanner', 'multi-scanner'])
  registerPanelColumns(
    type,
    storedColumns(async () => ({
      ...COLUMNS,
      defaults: DEFAULT_SCANNER_COLUMNS,
      columns: SCANNER_COLUMN_MAP,
    })),
  )

registerPanelFilters(
  'news',
  storedFilters('news', async () => {
    const { NEWS_COLUMN_MAP } = await import('@/features/news/schemas/news-column-defs')
    return { ...FILTERS, columns: NEWS_COLUMN_MAP }
  }),
)

// Ticker Stats keeps its date window as a rolling window, set from its header;
// a day0_date filter would be replaced by that window when the panel reads it.
registerPanelFilters(
  'ticker-stats',
  storedFilters('ticker-stats', async () => {
    const presets = await import('@/features/sliced-form/schemas/profile-presets')
    const preset = presets.DEFAULT_TICKER_STATS_FILTER_PROFILES.find(
      (p) => p.id === presets.DEFAULT_TICKER_STATS_FILTER_PROFILE_ID,
    )
    return {
      ...FILTERS,
      columns: HISTORY_COLUMN_MAP,
      // The default profile's own rows, in the form's shape.
      defaults: preset?.filters,
      fixed: {
        day0_date:
          "Ticker Stats keeps its date window in the header's date menu, as a rolling window; day0_date is not set as a filter there.",
      },
    }
  }),
)

registerPanelColumns(
  'ticker-stats',
  storedColumns(async () => ({
    ...COLUMNS,
    defaults: DEFAULT_RECORDS_COLUMNS,
    columns: HISTORY_COLUMN_MAP,
  })),
)

// The Heatmap and Multi-Chart scan the market on the Scanner's columns. Their
// own Apply names the scan with the filters; Multi-Chart shows its empty-scan
// prompt until one is named.
for (const type of ['sector-heatmap', 'multi-chart'])
  registerPanelFilters(
    type,
    storedFilters(type, async () => ({
      ...FILTERS,
      columns: SCANNER_COLUMN_MAP,
      alsoWrite: { scanName: 'Custom Scan' },
    })),
  )

// The Alert Window filters on the Scanner's columns (the matcher compiles the stored profile).
// A default window runs Liquid Stocks, so the AI's filters merge into it; a saved window keeps
// what it has, none included (only its symbols fire).
registerPanelFilters(
  'alert-windows',
  storedFilters('alert-windows', async (state) => {
    const { DEFAULT_ALERT_WINDOW_FILTER_PROFILES } = await import(
      '@/features/sliced-form/schemas/profile-presets'
    )
    const saved = Array.isArray(state.alerts) && state.alerts.length > 0
    return {
      ...FILTERS,
      columns: SCANNER_COLUMN_MAP,
      defaults: saved ? undefined : DEFAULT_ALERT_WINDOW_FILTER_PROFILES[0].filters,
      // The panel stores the form's shape; a query group would fail its schema on the next load.
      emptyFilters: [{ AND: [] }],
      // A saved window with none fires on its Also watch symbols only; a stored empty list would fire on every stock.
      clearKeepsNone: true,
    }
  }),
)

registerPanelColumns(
  'alert-windows',
  storedColumns(async () => {
    const { ALERT_WINDOW_COLUMN_CATALOG, DEFAULT_ALERT_WINDOW_COLUMNS } = await import(
      '@/features/alert-windows/alert-window-column-catalog'
    )
    return {
      ...COLUMNS,
      defaults: DEFAULT_ALERT_WINDOW_COLUMNS,
      columns: ALERT_WINDOW_COLUMN_CATALOG.columnMap,
    }
  }),
)

/** The Calendar's tab: each keeps its own filters and columns. */
function calendarTab(state: PanelState): 'earnings' | 'economic' | 'split' {
  const tab = state.currentTab
  return tab === 'economic' || tab === 'split' ? tab : 'earnings'
}

registerPanelFilters(
  'calendar',
  storedFilters('calendar', async (state) => {
    const tab = calendarTab(state)
    if (tab === 'economic')
      return {
        error:
          "The Calendar's Economic tab filters by country and impact in its own sheet; the AI sets filters on its Earnings and Splits tabs.",
      }
    const { DEFAULT_CALENDAR_FILTERS } = await import(
      '@/features/calendar/calendar-filter-defaults'
    )
    const slot = { columns: CALENDAR_COLUMN_MAP, defaults: DEFAULT_CALENDAR_FILTERS }
    return tab === 'split'
      ? {
          ...slot,
          filtersKey: 'splitsFilters',
          profileIdKey: 'splitsFilterProfileId',
          label: 'the Splits tab',
        }
      : {
          ...slot,
          filtersKey: 'earningsFilters',
          profileIdKey: 'earningsFilterProfileId',
          label: 'the Earnings tab',
        }
  }),
)

registerPanelColumns(
  'calendar',
  storedColumns(async (state) => {
    const defs = await import('@/features/calendar/column-defs')
    const tab = calendarTab(state)
    if (tab === 'economic')
      return {
        columnsKey: 'economicColumns',
        profileIdKey: 'economicColumnProfileId',
        defaults: defs.DEFAULT_ECONOMIC_COLUMNS,
        columns: defs.ECONOMIC_COLUMN_MAP,
        fixed: ['event'],
      }
    if (tab === 'split')
      return {
        columnsKey: 'splitsColumns',
        profileIdKey: 'splitsColumnProfileId',
        defaults: defs.DEFAULT_SPLITS_COLUMNS,
        columns: defs.SPLITS_COLUMN_MAP,
        fixed: ['ticker'],
      }
    return {
      columnsKey: 'earningsColumns',
      profileIdKey: 'earningsColumnProfileId',
      defaults: defs.DEFAULT_EARNINGS_COLUMNS,
      shown: defs.selectedEarningsColumns,
      columns: defs.EARNINGS_COLUMN_MAP,
      fixed: ['ticker'],
    }
  }),
)

registerPanelColumns(
  'history-records',
  storedColumns(async () => ({
    ...COLUMNS,
    defaults: DEFAULT_HISTORY_RECORDS_COLUMNS,
    columns: HISTORY_COLUMN_MAP,
  })),
)

// Tag Explorer's grid adds two columns of its own (outcome, value) to the
// history catalog, and still reads a saved r_multiple as value.
registerPanelColumns(
  'tags-explorer',
  storedColumns(async () => {
    const tags = await import('@/features/tags/panels/tag-item-columns')
    return {
      ...COLUMNS,
      defaults: tags.DEFAULT_TAG_ITEM_COLUMNS,
      shown: (stored) =>
        (stored ?? tags.DEFAULT_TAG_ITEM_COLUMNS).map((c) => (c === 'r_multiple' ? 'value' : c)),
      columns: {
        ...HISTORY_COLUMN_MAP,
        ...Object.fromEntries(tags.TAG_LOCAL_COLUMN_DEFS.map((c) => [c.key, c])),
      },
    }
  }),
)

// Theme Tracker's member table, on the Scanner's columns.
registerPanelColumns(
  'theme-tracker',
  storedColumns(async () => {
    const { THEME_MEMBER_COLUMNS } = await import('@/features/theme-tracker/theme-tracker-columns')
    return { ...COLUMNS, defaults: THEME_MEMBER_COLUMNS, columns: SCANNER_COLUMN_MAP }
  }),
)

registerPanelColumns(
  'watchlist',
  storedColumns(async () => {
    const { DEFAULT_WATCHLIST_COLUMNS } = await import('@/features/watchlist/watchlist-columns')
    return { ...COLUMNS, defaults: DEFAULT_WATCHLIST_COLUMNS, columns: SCANNER_COLUMN_MAP }
  }),
)

registerPanelColumns(
  'news',
  storedColumns(async () => {
    const news = await import('@/features/news/schemas/news-column-defs')
    return {
      ...COLUMNS,
      defaults: news.DEFAULT_NEWS_COLUMNS,
      // Retired keys can linger in saved state; the panel shows only known ones.
      shown: (stored) =>
        (stored ?? news.DEFAULT_NEWS_COLUMNS).filter((k) => news.NEWS_COLUMN_MAP[k] != null),
      columns: news.NEWS_COLUMN_MAP,
    }
  }),
)

// ── Capabilities no tool is gated on yet ────────────────────────────────────
//
// ticker, focus_date and link span panels too (spec 4.4). set_ticker,
// set_focus_date and set_link_mode act through the page's own callbacks on any
// panel, and none names a capability yet, so these declarations decide nothing:
// the catalog shows them. Each mirrors the panel chrome that already decides
// it (the drift test reads those sets from source): the header's ticker pill,
// its date picker or date display, and the Link action.

const TICKER_PANELS = [
  'chart',
  'key-stats',
  'news',
  'ticker-stats',
  'history-chart',
  'history-news',
  'backtest-trade-chart',
]
const FOCUS_DATE_PANELS = [
  'chart',
  'scanner',
  'multi-chart',
  'watchlist',
  'history-chart',
  'history-news',
  'backtest-trade-chart',
]
const LINK_PANELS = [
  'scanner',
  'sector-heatmap',
  'multi-scanner',
  'chart',
  'key-stats',
  'watchlist',
  'theme-tracker',
  'news',
  'ticker-stats',
  'calendar',
  'history-chart',
  'backtest-trade-chart',
  'history-heatmap',
  'history-time-distribution',
  'notebook',
  'multi-chart',
  'alert-windows',
]
for (const type of TICKER_PANELS) declarePanelCapability(type, 'ticker', ['change'])
for (const type of FOCUS_DATE_PANELS) declarePanelCapability(type, 'focus_date', ['change'])
for (const type of LINK_PANELS) declarePanelCapability(type, 'link', ['change'])
